import React, { useRef, useState, useEffect, useCallback } from 'react';
import { analyzePhoto, preloadPhotoCheck, fileToCanvas, flipCanvas, rotateCanvas, canvasToFile } from '../utils/photoCheck';

/**
 * ImageCapture — เลือกรูปได้ 2 ทาง: "ถ่ายรูป" (กล้องในหน้าเว็บ getUserMedia) และ "เลือกจากเครื่อง" (ไฟล์)
 * แก้ปัญหากล้องมืดจาก <input capture> โดยใช้กล้องสดในหน้าเว็บ + พรีวิว + มี fallback เลือกไฟล์เสมอ
 * รองรับ iOS/Android: video ใช้ playsInline+muted, เล่นหลัง set srcObject, ปิด track เมื่อถอด component
 *
 * props:
 *   onSelect(file: File)  — เรียกเมื่อได้รูป (ทั้งจากถ่ายและเลือกไฟล์)
 *   accept                — ชนิดไฟล์ที่รับ (ค่าเริ่ม image/*)
 *   captureLabel / browseLabel — ข้อความปุ่ม (ปรับได้)
 *   facingMode            — 'environment' (กล้องหลัง ถ่ายบัตร/เอกสาร, ค่าเริ่ม) | 'user' (กล้องหน้า ถ่ายตนเอง)
 *   overlay               — 'holdcard' = กรอบไกด์ถ่ายรูปตนเองถือบัตร (วงรีหน้า + กรอบบัตร) | ไม่ระบุ = ไม่มีกรอบ
 *   check                 — 'holdcard' | 'idcard' = ตรวจรูปเบื้องต้นในเครื่องผู้ใช้ก่อนส่งให้ onSelect (ดู utils/photoCheck.js)
 *                           ไม่ผ่าน = ไม่เรียก onSelect เลย (ต้องถ่าย/เลือกใหม่) · ผ่าน = แก้ภาพกลับด้าน/ตะแคงให้ แล้วให้ผู้ใช้ยืนยันรูป
 *                           ไม่ระบุ = ไม่ตรวจ (พฤติกรรมเดิม)
 */
// ผลตรวจที่ "ไม่มีทางผ่อนผัน" — ส่งแต่บัตร/ส่งรูปคนในช่องบัตร (ตัวตรวจหน้าใหญ่แม่นมาก ทดสอบกับรูปจริงไม่พลาดเลย)
// ส่วนผลอื่น (หาบัตรในรูปไม่เจอ) อาจพลาดได้กับรูปเบลอ/แสงสะท้อน → ไม่ผ่านครบ 3 ครั้ง ยอมให้ส่งต่อให้เจ้าหน้าที่ตรวจเอง
const STRICT_CODES = ['no-face', 'idcard-is-selfie'];
const MAX_SOFT_FAILS = 3;

function previewOf(canvas) {
    const k = Math.min(1, 900 / Math.max(canvas.width, canvas.height));
    const c = document.createElement('canvas');
    c.width = Math.round(canvas.width * k);
    c.height = Math.round(canvas.height * k);
    c.getContext('2d').drawImage(canvas, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.85);
}

// ฟิลเตอร์เซลฟี่ (โหมดกล้องหน้าเท่านั้น): เพิ่มแสง + สีสดใส + โทนอุ่นนวล ให้รูปดูดีขึ้น
// รูปบัตร/เอกสาร (กล้องหลัง) ไม่ใส่ฟิลเตอร์ — คงสีจริงเพื่อความถูกต้องของข้อมูล
const BEAUTY_FILTER = 'brightness(1.16) contrast(1.05) saturate(1.25) sepia(0.06)';

// เผื่อเบราว์เซอร์ที่ canvas ctx.filter ใช้ไม่ได้ (iOS Safari รุ่นเก่า) — ปรับ pixel ตรงๆ ให้ผลใกล้เคียงกัน
function applyBeautyPixels(ctx, width, height) {
    const imgData = ctx.getImageData(0, 0, width, height);
    const d = imgData.data;
    const B = 1.16;  // brightness
    const C = 1.05;  // contrast
    const S = 1.25;  // saturation
    for (let i = 0; i < d.length; i += 4) {
        // contrast รอบกึ่งกลาง แล้วค่อยเร่งแสง + โทนอุ่นเล็กน้อย (แดงขึ้นนิด น้ำเงินลงนิด)
        let r = ((d[i] - 128) * C + 128) * B * 1.03;
        let g = ((d[i + 1] - 128) * C + 128) * B * 1.005;
        let b = ((d[i + 2] - 128) * C + 128) * B * 0.97;
        const l = 0.299 * r + 0.587 * g + 0.114 * b; // luma
        r = l + (r - l) * S;
        g = l + (g - l) * S;
        b = l + (b - l) * S;
        d[i] = r > 255 ? 255 : (r < 0 ? 0 : r);
        d[i + 1] = g > 255 ? 255 : (g < 0 ? 0 : g);
        d[i + 2] = b > 255 ? 255 : (b < 0 ? 0 : b);
    }
    ctx.putImageData(imgData, 0, 0);
}

function ImageCapture({ onSelect, accept = 'image/*', captureLabel = 'ถ่ายรูป', browseLabel = 'เลือกจากเครื่อง', facingMode = 'environment', overlay, check }) {
    const fileInputRef = useRef(null);
    const nativeCamRef = useRef(null); // กล้องแอปเนทีฟของเครื่อง (fallback เสถียรสุดใน in-app browser)
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [camOpen, setCamOpen] = useState(false);
    const [starting, setStarting] = useState(false);
    const [camError, setCamError] = useState('');
    // หน้าตรวจ/ยืนยันรูป (เฉพาะเมื่อระบุ check): { status: 'checking' | 'pass' | 'fail' | 'unchecked', ... }
    const [review, setReview] = useState(null);
    const softFailsRef = useRef(0);

    const stopStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) videoRef.current.srcObject = null;
    }, []);

    // ปิดกล้องเมื่อ component ถูกถอด
    useEffect(() => () => stopStream(), [stopStream]);

    const openCamera = async () => {
        if (check) preloadPhotoCheck(); // โหลดตัวตรวจรูปรอไว้ระหว่างผู้ใช้จัดท่า
        setCamError('');
        setStarting(true);
        setCamOpen(true);
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('no-getusermedia');
            }
            let stream;
            try {
                // เลือกกล้องตามงาน: หลัง = ถ่ายบัตร/เอกสาร, หน้า = ถ่ายตนเอง(ถือบัตร)
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: facingMode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
                    audio: false
                });
            } catch (e) {
                // ถ้าไม่ได้ ใช้กล้องตัวไหนก็ได้
                stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            }
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                // iOS ต้อง playsInline + เล่นหลัง set srcObject
                await videoRef.current.play().catch(() => {});
            }
        } catch (err) {
            setCamError(
                'เปิดกล้องในหน้าเว็บไม่ได้ (พบบ่อยเมื่อเปิดจากแอป เช่น LINE) — ' +
                'กด "ถ่ายด้วยกล้องของเครื่อง" เพื่อใช้กล้องแอปมือถือแทน หรือเลือกรูปจากเครื่อง'
            );
        } finally {
            setStarting(false);
        }
    };

    const closeCamera = () => {
        stopStream();
        setCamOpen(false);
        setCamError('');
    };

    const takePhoto = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (facingMode === 'user') {
            // อบฟิลเตอร์ลงรูปจริงให้ตรงกับพรีวิว — ลอง ctx.filter ก่อน ถ้าเครื่องไม่รองรับใช้วิธีปรับ pixel
            ctx.filter = BEAUTY_FILTER;
            const filterSupported = ctx.filter !== 'none' && ctx.filter !== '';
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            ctx.filter = 'none';
            if (filterSupported) {
                // soft glow: ซ้อนเลเยอร์เบลอจางๆ ให้ผิวเนียนนวลแบบซอฟต์โฟกัส
                ctx.globalAlpha = 0.22;
                ctx.filter = `blur(${Math.max(4, Math.round(canvas.width / 300))}px) ${BEAUTY_FILTER}`;
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                ctx.filter = 'none';
                ctx.globalAlpha = 1;
            } else {
                try { applyBeautyPixels(ctx, canvas.width, canvas.height); } catch (e) { /* คงรูปเดิมไว้ */ }
            }
        } else {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        }
        canvas.toBlob(
            (blob) => {
                if (!blob) return;
                const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
                closeCamera();
                processFile(file);
            },
            'image/jpeg',
            0.92
        );
    };

    // ทุกรูป (กล้องในเว็บ / กล้องของเครื่อง / เลือกจากเครื่อง) ผ่านด่านเดียวกัน
    // กล้องหน้าของมือถือหลายรุ่นบันทึกเซลฟี่แบบกลับด้าน (mirror) → ตรวจจากตำแหน่งชิปบนบัตรแล้วกลับให้อัตโนมัติ
    const processFile = async (file) => {
        if (!check) { onSelect(file); return; }
        setReview({ status: 'checking' });
        const canvas = await fileToCanvas(file, 2000);
        if (!canvas) {
            // เบราว์เซอร์เปิดไฟล์นี้ไม่ได้ (เช่น HEIC บน Android) → ตรวจไม่ได้ ปล่อยให้เจ้าหน้าที่ตรวจ
            setReview(null);
            onSelect(file);
            return;
        }
        let result = null;
        try {
            result = await analyzePhoto(canvas, check);
        } catch (e) {
            result = null; // โหลดตัวตรวจไม่ได้ (เครื่องเก่า/เน็ตหลุด) → ไม่บล็อกผู้ใช้ แต่ยังให้ดู/กลับด้านรูปเองได้
        }
        if (result && !result.ok) {
            const strict = STRICT_CODES.includes(result.code);
            if (!strict) softFailsRef.current += 1;
            setReview({
                status: 'fail', file, canvas, preview: previewOf(canvas), message: result.message,
                allowOverride: !strict && softFailsRef.current >= MAX_SOFT_FAILS
            });
            return;
        }
        let work = canvas;
        let edited = false;
        let note = '';
        if (result && result.rotation) { work = rotateCanvas(work, result.rotation); edited = true; note = 'ระบบหมุนรูปให้ตั้งตรงแล้ว'; }
        if (result && result.mirrored === true) { work = flipCanvas(work); edited = true; note = 'รูปจากกล้องหน้ากลับด้านอยู่ — ระบบกลับด้านให้แล้ว'; }
        setReview({ status: result ? 'pass' : 'unchecked', file, canvas: work, edited, note, preview: previewOf(work) });
    };

    // ผู้ใช้แก้รูปเองในหน้ายืนยัน (เผื่อระบบตัดสินกลับด้าน/ทิศไม่ได้หรือผิด)
    const editReview = (transform) => {
        setReview((r) => {
            if (!r || !r.canvas) return r;
            const next = transform(r.canvas);
            return { ...r, canvas: next, edited: true, note: '', preview: previewOf(next) };
        });
    };
    const flipReview = () => editReview(flipCanvas);
    const rotateReview = () => editReview((c) => rotateCanvas(c, 90));

    const confirmReview = async () => {
        const r = review;
        if (!r || !r.file) return;
        let file = r.file;
        if (r.edited) {
            try { file = await canvasToFile(r.canvas, `photo-${Date.now()}.jpg`); } catch (e) { file = r.file; }
        }
        softFailsRef.current = 0;
        setReview(null);
        onSelect(file);
    };

    const retake = (how) => {
        setReview(null);
        if (how === 'browse') { if (fileInputRef.current) fileInputRef.current.click(); } else openCamera();
    };

    const handleFile = (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) processFile(file);
        e.target.value = '';
    };

    return (
        <div className="imgcap">
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" className="steam-btn steam-btn-primary" onClick={openCamera}>
                    <span style={{ marginRight: 6 }}>📷</span>{captureLabel}
                </button>
                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                    <span style={{ marginRight: 6 }}>🖼️</span>{browseLabel}
                </button>
                <input ref={fileInputRef} type="file" accept={accept} onChange={handleFile} style={{ display: 'none' }} />
                {/* กล้องแอปเนทีฟ: input capture เปิดกล้องของเครื่องตรงๆ — ใช้ได้แม้ใน in-app browser (LINE ฯลฯ) */}
                <input
                    ref={nativeCamRef}
                    type="file"
                    accept="image/*"
                    capture={facingMode === 'user' ? 'user' : 'environment'}
                    onChange={handleFile}
                    style={{ display: 'none' }}
                />
            </div>

            {camOpen && (
                <div
                    onClick={closeCamera}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        // โหมดเซลฟี่: พื้นหลังสว่างทำหน้าที่เป็น "ไฟส่องหน้า" จากจอ ช่วยให้หน้าสว่างขึ้นในที่แสงน้อย
                        background: facingMode === 'user' ? 'rgba(255,252,245,0.97)' : 'rgba(0,0,0,0.85)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: facingMode === 'user' ? '#fffcf5' : '#000',
                            borderRadius: 12, maxWidth: 640, width: '100%', overflow: 'hidden',
                            // ขอบสว่างรอบวิดีโอ = ring light จากจอ ช่วยเพิ่มแสงบนใบหน้า
                            border: facingMode === 'user' ? '10px solid #fffcf5' : 'none',
                            boxShadow: facingMode === 'user' ? '0 6px 30px rgba(0,0,0,0.15)' : 'none'
                        }}
                    >
                        {camError ? (
                            <div style={{ padding: 24, color: '#fca5a5', fontSize: 14, lineHeight: 1.7, textAlign: 'center', background: '#1f2937' }}>
                                {camError}
                            </div>
                        ) : (
                            <div style={{ position: 'relative' }}>
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    style={{
                                        width: '100%', maxHeight: '70vh', display: 'block', background: '#000', objectFit: 'contain',
                                        // พรีวิวไม่กลับด้าน (ไม่ใช่แบบกระจก) — ตัวหนังสือบนบัตรอ่านออกตั้งแต่ตอนพรีวิว = รูปที่บันทึก
                                        filter: facingMode === 'user' ? BEAUTY_FILTER : 'none'
                                    }}
                                />
                                {overlay === 'holdcard' && (
                                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden="true">
                                        {/* วงรีไกด์ตำแหน่งใบหน้า */}
                                        <div style={{
                                            position: 'absolute', top: '6%', left: '50%', transform: 'translateX(-50%)',
                                            width: '38%', height: '48%', border: '3px dashed rgba(255,255,255,0.9)',
                                            borderRadius: '50%', boxShadow: '0 0 0 2000px rgba(0,0,0,0.25)'
                                        }} />
                                        {/* กรอบไกด์ตำแหน่งบัตรประชาชน (ถือไว้ระดับอก ใต้ใบหน้า) */}
                                        <div style={{
                                            position: 'absolute', bottom: '14%', left: '50%', transform: 'translateX(-50%)',
                                            width: '52%', aspectRatio: '1.586 / 1', border: '3px dashed rgba(74,222,128,0.95)',
                                            borderRadius: 10
                                        }} />
                                        <div style={{
                                            position: 'absolute', bottom: '3%', left: 0, right: 0, textAlign: 'center',
                                            color: '#fff', fontSize: 13, fontWeight: 600, textShadow: '0 1px 4px rgba(0,0,0,0.9)', padding: '0 12px'
                                        }}>
                                            จัดใบหน้าให้อยู่ในวงรี และถือบัตรให้อยู่ในกรอบเขียว — เห็นข้อมูลบัตรชัดเจน
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', padding: 14, background: facingMode === 'user' ? '#fffcf5' : '#111827', flexWrap: 'wrap' }}>
                            {!camError && (
                                <button type="button" className="steam-btn steam-btn-primary" onClick={takePhoto} disabled={starting}>
                                    {starting ? 'กำลังเปิดกล้อง...' : '📸 ถ่าย'}
                                </button>
                            )}
                            {camError && (
                                <>
                                    <button type="button" className="steam-btn steam-btn-primary" onClick={() => { closeCamera(); nativeCamRef.current && nativeCamRef.current.click(); }}>
                                        📷 ถ่ายด้วยกล้องของเครื่อง
                                    </button>
                                    <button type="button" className="steam-btn steam-btn-secondary" onClick={() => { closeCamera(); fileInputRef.current && fileInputRef.current.click(); }}>
                                        🖼️ {browseLabel}
                                    </button>
                                </>
                            )}
                            <button type="button" className="steam-btn steam-btn-secondary" onClick={closeCamera}>ปิด</button>
                        </div>
                    </div>
                </div>
            )}

            {review && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.85)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
                }}>
                    <div style={{ background: '#fff', borderRadius: 12, maxWidth: 520, width: '100%', maxHeight: '94vh', overflowY: 'auto' }}>
                        {review.status === 'checking' ? (
                            <div style={{ padding: '40px 24px', textAlign: 'center', color: '#374151', fontSize: 15 }}>
                                🔎 กำลังตรวจสอบรูป...
                                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>ตรวจในเครื่องของท่าน ใช้เวลาไม่กี่วินาที</div>
                            </div>
                        ) : (
                            <>
                                <img src={review.preview} alt="รูปที่เลือก" style={{ width: '100%', maxHeight: '56vh', objectFit: 'contain', display: 'block', background: '#000', borderRadius: '12px 12px 0 0' }} />
                                <div style={{ padding: 16 }}>
                                    {review.status === 'fail' ? (
                                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: 12, fontSize: 14, lineHeight: 1.7 }}>
                                            <strong>❌ รูปนี้ยังใช้ไม่ได้</strong><br />{review.message}
                                        </div>
                                    ) : (
                                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: 12, fontSize: 14, lineHeight: 1.7 }}>
                                            {review.status === 'pass' && <><strong>✓ ผ่านการตรวจเบื้องต้น</strong><br /></>}
                                            {review.note && <>{review.note}<br /></>}
                                            ตรวจดูว่า <strong>ตัวหนังสือบนบัตรอ่านออกตามปกติ</strong> (ไม่กลับด้าน ไม่ตะแคง) และภาพชัด — ถ้ายังไม่ถูก กด "กลับด้านภาพ" หรือ "หมุนภาพ"
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 14 }}>
                                        {review.status !== 'fail' && (
                                            <>
                                                <button type="button" className="steam-btn steam-btn-primary" onClick={confirmReview}>✓ ใช้รูปนี้</button>
                                                <button type="button" className="steam-btn steam-btn-secondary" onClick={flipReview}>↔️ กลับด้านภาพ</button>
                                                <button type="button" className="steam-btn steam-btn-secondary" onClick={rotateReview}>⟳ หมุนภาพ</button>
                                            </>
                                        )}
                                        <button type="button" className={`steam-btn ${review.status === 'fail' ? 'steam-btn-primary' : 'steam-btn-secondary'}`} onClick={() => retake('camera')}>📷 ถ่ายใหม่</button>
                                        <button type="button" className="steam-btn steam-btn-secondary" onClick={() => retake('browse')}>🖼️ เลือกรูปอื่น</button>
                                        {review.status === 'fail' && (
                                            <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setReview(null)}>ปิด</button>
                                        )}
                                    </div>
                                    {review.status === 'fail' && review.allowOverride && (
                                        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 13, color: '#6b7280' }}>
                                            มั่นใจว่ารูปถูกต้องแล้ว?{' '}
                                            <button type="button" onClick={confirmReview} style={{ background: 'none', border: 'none', color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 }}>
                                                ส่งรูปนี้ให้เจ้าหน้าที่ตรวจสอบเอง
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImageCapture;
